'use client';

import React, { useState, useEffect } from 'react';
import PlanCard from './PlanCard';
import FilterControls from './FilterControls';
import SearchBar from './SearchBar';
import BulkActions from './BulkActions';
import EmptyState from './EmptyState';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { planService } from '../../../services/planService';
import { Plan } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface FilterOptions {
  spaceType: string;
  status: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export default function SavedPlansInteractive() {
  const { user } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [filteredPlans, setFilteredPlans] = useState<Plan[]>([]);
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<FilterOptions>({
    spaceType: '',
    status: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadPlans = async () => {
      if (!user) return;
      try {
        setLoading(true);
        setError('');
        const { data, error } = await planService.getAll(user.id);
        
        if (error) {
          setError(error.message);
          return;
        }
        
        setPlans(data || []);
      } catch (err) {
        setError('Failed to load plans. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadPlans();
  }, [user]);

  // Filter and sort plans
  useEffect(() => {
    let filtered = [...plans];

    // Apply search filter
    if (searchTerm?.trim()) {
      filtered = filtered.filter(plan =>
        plan?.objective?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
        plan?.location?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
        plan?.details?.toLowerCase()?.includes(searchTerm.toLowerCase())
      );
    }

    // Apply space type filter
    if (filters.spaceType) {
      filtered = filtered.filter(plan => {
        const location = plan?.location?.toLowerCase() || '';
        return location.includes(filters.spaceType.toLowerCase());
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (filters.sortBy) {
        case 'createdAt':
          comparison = new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
          break;
        case 'name':
          comparison = (a?.objective || '').localeCompare(b?.objective || '');
          break;
        case 'income':
          const budgetA = parseFloat((a?.budget || '0').replace(/[^0-9.]/g, '')) || 0;
          const budgetB = parseFloat((b?.budget || '0').replace(/[^0-9.]/g, '')) || 0;
          comparison = budgetA - budgetB;
          break;
        default:
          comparison = new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
      }

      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredPlans(filtered);
  }, [plans, searchTerm, filters]);

  const handleFilterChange = (newFilters: FilterOptions) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      spaceType: '',
      status: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
  };

  const handleSelectPlan = (planId: string) => {
    setSelectedPlans(prev =>
      prev.includes(planId)
        ? prev.filter(id => id !== planId)
        : [...prev, planId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPlans.length === filteredPlans.length) {
      setSelectedPlans([]);
    } else {
      setSelectedPlans(filteredPlans?.map(plan => plan?.id) || []);
    }
  };

  const handleDeleteClick = (planId: string) => {
    setPlanToDelete(planId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!planToDelete) return;

    try {
      const { error } = await planService.delete(planToDelete);
      
      if (error) {
        setError(error.message);
        return;
      }

      setPlans(prev => prev.filter(plan => plan?.id !== planToDelete));
      setSelectedPlans(prev => prev.filter(id => id !== planToDelete));
    } catch (err) {
      setError('Failed to delete plan. Please try again.');
    } finally {
      setShowDeleteModal(false);
      setPlanToDelete(null);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(
        selectedPlans.map(planId => planService.delete(planId))
      );

      setPlans(prev => prev.filter(plan => !selectedPlans.includes(plan?.id || '')));
      setSelectedPlans([]);
    } catch (err) {
      setError('Failed to delete some plans. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Saved Plans</h1>
          <p className="text-gray-600">View and manage your space optimization plans</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6 mb-8">
          <div className="flex-1">
            <SearchBar 
              onSearch={setSearchTerm} 
            />
          </div>
        </div>

        <FilterControls
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          totalPlans={plans.length}
          filteredPlans={filteredPlans.length}
        />

        {selectedPlans.length > 0 && (
          <BulkActions
            selectedPlans={selectedPlans}
            onBulkDelete={() => handleBulkDelete()}
            onCompare={() => {}}
            onBulkExport={() => {}}
            onDeselectAll={() => setSelectedPlans([])}
          />
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading plans...</span>
          </div>
        ) : filteredPlans.length === 0 ? (
          <EmptyState isFiltered={!!searchTerm} onClearFilters={handleClearFilters} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPlans?.map(plan => (
              plan && (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isSelected={selectedPlans.includes(plan.id)}
                  onSelect={() => handleSelectPlan(plan.id)}
                  onDelete={() => handleDeleteClick(plan.id)}
                />
              )
            ))}
          </div>
        )}

        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          planName={plans.find(p => p?.id === planToDelete)?.objective || 'this plan'}
        />
      </div>
    </div>
  );
}